import React from 'react'
import { TextClamp } from '../components/text/TextClamp'
import { FitText } from '../components/text/FitText'
import { TEXT_CONSTRAINTS } from '../foundations/text-constraints'
import { sanitizeNarrativeText } from '../foundations/text-sanitize'
import { EditableText } from '../slides/EditableText'

export interface SlideHeadlineProps {
  actionTitle?: string
  actionSubtitle?: string
  /**
   * The host layout's headline and subtitle line budgets (plots plan Step 7: a stage
   * preset's bound slots). Default: the exec-summary budgets this headline always used.
   */
  maxLines?: { title: number; subtitle?: number }
  editable?: boolean
  onEdit?: (field: string, value: string) => void
}

export function SlideHeadline({ actionTitle, actionSubtitle, maxLines, editable, onEdit }: SlideHeadlineProps) {
  const resolvedActionTitle = sanitizeNarrativeText(actionTitle)
  const hasContent = Boolean(resolvedActionTitle || actionSubtitle)
  if (!hasContent) return null

  const titleClamp = TEXT_CONSTRAINTS.slideActionTitle
  const subtitleClamp = TEXT_CONSTRAINTS.slideActionSubtitle

  return (
    <header data-testid="slide-headline" style={{ width: '100%', marginBottom: 'var(--ds-gap-xl)', overflow: 'visible', flexShrink: 0 }}>
      {resolvedActionTitle && (
        editable && onEdit ? (
          <EditableText
            value={resolvedActionTitle}
            field="actionTitle"
            editable
            onEdit={onEdit}
            tag="h1"
            style={{
              fontFamily: 'var(--ds-font-display)',
              fontSize: 'var(--ds-fs-exec-headline)',
              fontWeight: 'var(--ds-weight-regular)' as any,
              lineHeight: 'var(--ds-lh-snug)',
              color: 'var(--ds-text-display-headline)',
              textWrap: 'balance' as any,
              marginBottom: actionSubtitle ? 'var(--ds-gap-sm)' : 0,
            }}
          />
        ) : (
          <FitText
            as="h1"
            maxLines={maxLines?.title ?? titleClamp.maxLines}
            baseFontSize={40}
            minFontSize={26}
            lineHeight={1.2}
            style={{
              fontFamily: 'var(--ds-font-display)',
              fontWeight: 'var(--ds-weight-regular)' as any,
              color: 'var(--ds-text-display-headline)',
              textWrap: 'balance' as any,
              marginBottom: actionSubtitle ? 'var(--ds-gap-sm)' : 0,
            }}
          >
            {resolvedActionTitle}
          </FitText>
        )
      )}
      {actionSubtitle && (
        editable && onEdit ? (
          <EditableText
            value={actionSubtitle}
            field="actionSubtitle"
            editable
            onEdit={onEdit}
            tag="p"
            style={{
              fontSize: 'var(--ds-fs-subtitle-sm)',
              color: 'var(--ds-text-body)',
            }}
          />
        ) : (
          <TextClamp
            as="p"
            minLines={subtitleClamp.minLines}
            maxLines={maxLines?.subtitle ?? subtitleClamp.maxLines}
            lineHeight={subtitleClamp.lineHeight}
            style={{
              fontSize: 'var(--ds-fs-subtitle-sm)',
              color: 'var(--ds-text-body)',
            }}
          >
            {actionSubtitle}
          </TextClamp>
        )
      )}
    </header>
  )
}
